import React, { useState } from 'react'
import {
    Box,
    Collapse,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
} from '@mui/material'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AssessmentIcon from '@mui/icons-material/Assessment'
import { Link, useLocation } from 'react-router-dom'
import { RouteLink, routeLinks } from '../../Router/routeLinks';

interface SideBarProps {
    width: number;
}

/** Icon shown next to a collapsible sidebar group header, keyed by RouteLink.sidebarGroup. */
const groupIcons: Record<string, React.ReactNode> = {
    Raporty: <AssessmentIcon fontSize="small" />,
};

const getSidebarRoutes = (links: RouteLink[]): RouteLink[] =>
    links.reduce<RouteLink[]>((acc, link) => {
        if (link.showInSidebar !== false && link.label) {
            acc.push(link);
        }
        if (link.children) {
            acc = acc.concat(getSidebarRoutes(link.children));
        }
        return acc;
    }, []);

interface SidebarGroup {
    name: string;
    items: RouteLink[];
}

/** Partitions flat sidebar links into ungrouped items and named groups, preserving order of first appearance. */
const partitionSidebarLinks = (
    links: RouteLink[]
): { ungrouped: RouteLink[]; groups: SidebarGroup[] } => {
    const ungrouped: RouteLink[] = [];
    const groups: SidebarGroup[] = [];
    const groupIndexByName = new Map<string, number>();

    links.forEach((link) => {
        if (!link.sidebarGroup) {
            ungrouped.push(link);
            return;
        }

        let index = groupIndexByName.get(link.sidebarGroup);
        if (index === undefined) {
            index = groups.length;
            groupIndexByName.set(link.sidebarGroup, index);
            groups.push({ name: link.sidebarGroup, items: [] });
        }
        groups[index].items.push(link);
    });

    return { ungrouped, groups };
};

const SideBar = ({ width }: SideBarProps) => {
    const location = useLocation();
    const sidebarLinks = getSidebarRoutes(routeLinks);
    const { ungrouped, groups } = partitionSidebarLinks(sidebarLinks);

    const groupContainsActivePath = (group: SidebarGroup) =>
        group.items.some((item) => item.path === location.pathname);

    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() =>
        groups.reduce<Record<string, boolean>>((acc, group) => {
            acc[group.name] = groupContainsActivePath(group);
            return acc;
        }, {})
    );

    const toggleGroup = (name: string) => {
        setOpenGroups((prev) => ({ ...prev, [name]: !prev[name] }));
    };

    return (
        <Drawer
            variant="permanent"
            sx={{
                width: width,
                flexShrink: 0,
                [`& .MuiDrawer-paper`]: { width: width, boxSizing: 'border-box' },
            }}
        >
            <Toolbar />
            <Box sx={{ overflow: 'auto' }}>
                <List>
                    {ungrouped.map((item) => (
                        <ListItem key={item.path} disablePadding>
                            <ListItemButton component={Link} to={item.path}>
                                <ListItemText primary={item.label} />
                            </ListItemButton>
                        </ListItem>
                    ))}

                    {groups.map((group) => {
                        const isOpen = openGroups[group.name] ?? false;

                        return (
                            <React.Fragment key={group.name}>
                                <ListItem disablePadding>
                                    <ListItemButton
                                        onClick={() => toggleGroup(group.name)}
                                        data-testid={`sidebar-group-${group.name}`}
                                    >
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            {groupIcons[group.name]}
                                        </ListItemIcon>
                                        <ListItemText primary={group.name} />
                                        {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </ListItemButton>
                                </ListItem>
                                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {group.items.map((item) => (
                                            <ListItem key={item.path} disablePadding>
                                                <ListItemButton
                                                    component={Link}
                                                    to={item.path}
                                                    sx={{ pl: 4 }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                                        {item.icon}
                                                    </ListItemIcon>
                                                    <ListItemText primary={item.label} />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Collapse>
                            </React.Fragment>
                        );
                    })}
                </List>
            </Box>
        </Drawer>
    )
}

export default SideBar
